/**
 * The following functions need to use `null` as this is part of the XML DOM API
 */

/**
 * Helper function to find an XML node with an XPath expression. If multiple nodes match the expression, get the first one.
 *
 * @param doc xml document
 * @param context the context node
 * @param path the path to the node
 * @returns {(Element|null)} returns the found element or null
 */
export function getFirstNode(doc: XMLDocument, context: Node, path: string): Element | null {
    const nodes = doc.evaluate(path, context, null, XPathResult.ORDERED_NODE_SNAPSHOT_TYPE, null);
    return nodes.snapshotItem(0) as Element;
}

/**
 * Helper function to find an XML node with an XPath expression. If multiple nodes match the expression, get the last one.
 *
 * @param doc xml document
 * @param context the context node
 * @param path the path to the node
 * @returns {(Element|null)} returns the found element or null
 */
export function getLastNode(doc: XMLDocument, context: Node, path: string): Element | null {
    const nodes = doc.evaluate(path, context, null, XPathResult.ORDERED_NODE_SNAPSHOT_TYPE, null);
    return nodes.snapshotItem(nodes.snapshotLength - 1) as Element;
}

/**
 * Helper function to get all children matching an XPath expression from a node.
 *
 * @param doc xml document
 * @param context the context node
 * @param path the path to the node
 * @returns {(Element[])} returns the found children elements as array
 */
export function getChildNodes(doc: XMLDocument, context: Node, path: string): Element[] {
    const nodeIterator = doc.evaluate(path, context, null, XPathResult.ORDERED_NODE_ITERATOR_TYPE, null);
    const nodes: Element[] = [];
    let node = nodeIterator.iterateNext();
    while (node !== null) {
        nodes.push(node as Element);
        node = nodeIterator.iterateNext();
    }

    return nodes;
}

/**
 * Checks if the DOM parser was able to read the XML successfully
 *
 * @param doc xml document
 * @returns boolean for errors found in document
 */
export function checkForErrorDocument(doc: XMLDocument): boolean {
    // Works, but does not use XPath :(
    return doc.getElementsByTagName('parsererror').length === 0;
}
